import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { PagesQuery } from "../../api/pages";
import {
  PageCloneDTO,
  PageSettingsDTO,
  PagesData,
  PagesResponseListDto,
} from "../../models/Pages";
import { ProjectDTO } from "../../models/Projects";
import { NavigateFunction } from "react-router/dist/lib/hooks";
import {
  ICreateKpi,
  IDeleteKpi,
  IUpdateKpi,
  KpiItemsDTO,
  QueryGetKpis,
  QueryGetWidgets,
  WidgetItem,
  WidgetsItemsDTO,
} from "../../models/Widgets";
import { RequestCallbacks } from "../../models";
import { defaultHeader } from "../../components/AddStorytellingSection";
import {
  ICreateSection,
  IRemoveSection,
  IUpdateSection,
  QueryGetSections,
} from "../../api/sections";

export const initialSettings = {
  name: "",
  dashType: "",
  projectId: "",
  templateId: "",
  interactivity: [],
  styleId: "",
  showFilters: false,
  showSearch: false,
  showTooltip: true,
  header: defaultHeader,
};

const initialState: PagesResponseListDto = {
  pagesData: {
    items: [],
    isLoading: false,
    count: 0,
    currentPage: {
      settings: initialSettings,
      draftSettings: initialSettings,
      widgets: {
        items: [],
        count: 0,
      },
      kpis: { items: [], count: 0 },
      sectionsCount: 0,
      isLoading: false,
    },
  },
  id: "",
  projectData: { id: "", name: "", description: "", datasets: [], pages: [] },
  widget: undefined,
};

const projectPages = createSlice({
  name: "currentProject",
  initialState,
  reducers: {
    requestAllPages: (
      state: PagesResponseListDto,
      action: PayloadAction<Partial<PagesQuery>>
    ) => {},

    requestAllPublicPages: (
      state: PagesResponseListDto,
      action: PayloadAction<Partial<PagesQuery>>
    ) => {},

    requestCreatePage: (
      state: PagesResponseListDto,
      action: PayloadAction<PageSettingsDTO>
    ) => {},
    
    requestClonePage: (
      state: PagesResponseListDto,
      action: PayloadAction<PageCloneDTO>
    ) => {},

    requestPageWidgets: (state, action: PayloadAction<QueryGetWidgets>) => {},

    requestPublicPageWidgets: (
      state,
      action: PayloadAction<QueryGetWidgets>
    ) => {},

    updatePagesItems: (
      state: PagesResponseListDto,
      action: PayloadAction<PageSettingsDTO>
    ): PagesResponseListDto => {
      return {
        ...state,
        pagesData: {
          ...state.pagesData,
          items: state.pagesData.items
            ? [...state.pagesData.items, action.payload]
            : [action.payload],
          count: state.pagesData.count + 1,
        },
      };
    },

    resetCurrentPageData: (state) => ({
      ...state,
      pagesData: {
        ...state.pagesData,
        currentPage: initialState.pagesData.currentPage,
      },
    }),

    resetPagesDataItems: (state) => ({
      ...state,
      pagesData: {
        ...state.pagesData,
        items: initialState.pagesData.items,
      },
    }),

    setCurrentProjectId: (
      state: PagesResponseListDto,
      action: PayloadAction<string>
    ): PagesResponseListDto => {
      return {
        ...state,
        id: action.payload,
      };
    },

    setCurrentProjectData: (
      state: PagesResponseListDto,
      action: PayloadAction<ProjectDTO>
    ) => {
      return {
        ...state,
        projectData: action.payload,
      };
    },

    requestPageById: (
      state: PagesResponseListDto,
      action: PayloadAction<{ id: string; callbacks?: RequestCallbacks<any> }>
    ) => {},

    requestPublicPageById: (
      state: PagesResponseListDto,
      action: PayloadAction<{ id: string; callbacks?: RequestCallbacks<any> }>
    ) => {},

    requestRemovePage: (
      state: PagesResponseListDto,
      action: PayloadAction<{ id: string; navigate: NavigateFunction }>
    ) => {},

    requestUpdatePage: (
      state: PagesResponseListDto,
      action: PayloadAction<PageSettingsDTO>
    ) => {},

    requestUpdatePageInBackgound: (
      state: PagesResponseListDto,
      action: PayloadAction<PageSettingsDTO>
    ) => {},

    setIsLoading: (
      state: PagesResponseListDto,
      action: PayloadAction<boolean>
    ): PagesResponseListDto => {
      return {
        ...state,
        pagesData: {
          ...state.pagesData,
          isLoading: action.payload,
        },
      };
    },

    setCurrentPageIsLoading: (
      state: PagesResponseListDto,
      action: PayloadAction<boolean>
    ) => {
      state.pagesData.currentPage.isLoading = action.payload;
    },

    setAllPages: (
      state: PagesResponseListDto,
      action: PayloadAction<PagesData>
    ): PagesResponseListDto => {
      return {
        ...state,
        pagesData: {
          ...state.pagesData,
          count: action.payload.count,
          items: action.payload.items,
        },
      };
    },

    setPageSettings: (
      state: PagesResponseListDto,
      action: PayloadAction<PageSettingsDTO>
    ): PagesResponseListDto => {
      return {
        ...state,
        pagesData: {
          ...state.pagesData,
          currentPage: {
            ...state.pagesData.currentPage,
            settings: action.payload,
          },
        },
      };
    },

    requestPageSections: (state, action: PayloadAction<QueryGetSections>) => {},

    requestPublicPageSections: (state, action: PayloadAction<QueryGetSections>) => {},

    setPageSections: (state, action: PayloadAction<number>) => {
      return {
        ...state,
        pagesData: {
          ...state.pagesData,
          currentPage: {
            ...state.pagesData.currentPage,
            sectionsCount: action.payload,
          },
        },
      };
    },

    requestCreatePageSections: (
      state,
      action: PayloadAction<ICreateSection>
    ) => {},

    requestUpdatePageSections: (
      state,
      action: PayloadAction<IUpdateSection>
    ) => {},

    requestRemovePageSections: (
      state,
      action: PayloadAction<IRemoveSection>
    ) => {},

    resetPageSettings: (state: PagesResponseListDto): PagesResponseListDto => {
      return {
        ...state,
        pagesData: {
          ...state.pagesData,
          currentPage: {
            ...state.pagesData.currentPage,
            settings: initialSettings,
            sectionsCount: 0,
          },
        },
      };
    },

    setDraftPageSettings: (
      state: PagesResponseListDto,
      action: PayloadAction<PageSettingsDTO>
    ): PagesResponseListDto => {
      return {
        ...state,
        pagesData: {
          ...state.pagesData,
          currentPage: {
            ...state.pagesData.currentPage,
            draftSettings: action.payload,
          },
        },
      };
    },

    setPageWidgets: (
      state: PagesResponseListDto,
      action: PayloadAction<WidgetsItemsDTO>
    ): PagesResponseListDto => {
      return {
        ...state,
        pagesData: {
          ...state.pagesData,
          currentPage: {
            ...state.pagesData.currentPage,
            widgets: {
              ...state.pagesData.currentPage.widgets,
              ...action.payload,
            },
          },
        },
      };
    },

    removePage: (
      state: PagesResponseListDto,
      action: PayloadAction<string>
    ): PagesResponseListDto => {
      return {
        ...state,
        pagesData: {
          ...state.pagesData,
          items: state.pagesData.items?.filter(
            (item) => item.id !== action.payload
          ),
          count: state.pagesData.count - 1,
        },
      };
    },

    resetPages: (state: PagesResponseListDto): PagesResponseListDto => {
      return {
        ...state,
        pagesData: initialState.pagesData,
      };
    },

    resetDraftSettings: (state: PagesResponseListDto): PagesResponseListDto => {
      return {
        ...state,
        pagesData: {
          ...state.pagesData,
          currentPage: {
            ...state.pagesData.currentPage,
            draftSettings: initialSettings,
          },
        },
      };
    },

    updatePageName: (
      state: PagesResponseListDto,
      action: PayloadAction<{ id: string; name: string }>
    ): PagesResponseListDto => {
      return {
        ...state,
        pagesData: {
          ...state.pagesData,
          items: state.pagesData.items.map((item) =>
            item.id === action.payload.id
              ? { ...item, name: action.payload.name }
              : item
          ),
        },
      };
    },

    requestPublicProjectPagesId: (
      state: PagesResponseListDto,
      action: PayloadAction<string>
    ) => {},

    requestPublicProjectPageById: (
      state: PagesResponseListDto,
      action: PayloadAction<{
        id: string;
        pageId: string;
      }>
    ) => {},

    setCurrentWidget: (
      state: PagesResponseListDto,
      action: PayloadAction<WidgetItem | undefined>
    ) => {
      return {
        ...state,
        widget: action.payload,
      };
    },

    resetWidgetsData: (state) => {
      return {
        ...state,
        pagesData: {
          ...state.pagesData,
          currentPage: {
            ...state.pagesData.currentPage,
            widgets: { items: [], count: 0 },
          },
        },
      };
    },

    setKpiWidgets: (
      state: PagesResponseListDto,
      action: PayloadAction<KpiItemsDTO>
    ): PagesResponseListDto => {
      return {
        ...state,
        pagesData: {
          ...state.pagesData,
          currentPage: {
            ...state.pagesData.currentPage,
            kpis: {
              ...state.pagesData.currentPage.kpis,
              ...action.payload,
            },
          },
        },
      };
    },

    requestPageKpis: (state, action: PayloadAction<QueryGetKpis>) => {},
    requestPagePublicKpis: (state, action: PayloadAction<QueryGetKpis>) => {},
    requestCreateKpis: (state, action: PayloadAction<ICreateKpi>) => {},
    requestUpdateKpis: (state, action: PayloadAction<IUpdateKpi>) => {},
    requestDeleteKpis: (state, action: PayloadAction<IDeleteKpi>) => {},
  },
});

export const {
  resetWidgetsData,
  requestAllPages,
  requestCreatePage,
  requestClonePage,
  requestPublicPageById,
  requestPublicPageWidgets,
  requestAllPublicPages,
  requestPageById,
  requestRemovePage,
  requestPageWidgets,
  setIsLoading,
  setAllPages,
  setPageSettings,
  resetPages,
  removePage,
  requestUpdatePage,
  updatePageName,
  setCurrentProjectId,
  setCurrentProjectData,
  resetDraftSettings,
  resetPageSettings,
  setDraftPageSettings,
  updatePagesItems,
  setPageWidgets,
  requestPublicProjectPagesId,
  requestPublicProjectPageById,
  resetCurrentPageData,
  resetPagesDataItems,
  requestPageSections,
  requestPublicPageSections,
  setPageSections,
  requestCreatePageSections,
  requestUpdatePageSections,
  requestRemovePageSections,
  setCurrentWidget,
  setCurrentPageIsLoading,
  requestPageKpis,
  requestPagePublicKpis,
  requestCreateKpis,
  requestUpdateKpis,
  requestDeleteKpis,
  setKpiWidgets,
  requestUpdatePageInBackgound,
} = projectPages.actions;
export default projectPages.reducer;
